import { z } from 'zod'

// Loan type constants (matching Symfony - see assets/js/stores/loan/model/loan_type.js)
export const LOAN_TYPES = {
    CLASSIC: 1, // Prêt classique / amortissable
    IN_FINE: 2, // Prêt in fine
    STAGE: 3, // Prêt à paliers
    RELAY: 4, // Prêt relais
    BUILDING: 5, // Prêt construction (VEFA, travaux)
} as const

export type LoanTypeValue = (typeof LOAN_TYPES)[keyof typeof LOAN_TYPES]

// Delay type constants
export const DELAY_TYPES = {
    PARTIAL: 'partial', // Différé partiel - remboursement des intérêts seulement
    TOTAL: 'total', // Différé total - aucun remboursement
} as const

export type DelayTypeValue = (typeof DELAY_TYPES)[keyof typeof DELAY_TYPES]

// Currency constants
export const CURRENCIES = {
    EUR: 'euro',
    CHF: 'chf',
} as const

export type CurrencyValue = (typeof CURRENCIES)[keyof typeof CURRENCIES]

export const CURRENCY_SYMBOLS: Record<CurrencyValue, string> = {
    [CURRENCIES.EUR]: '€',
    [CURRENCIES.CHF]: 'CHF',
}

export const currencySymbol = (currency: CurrencyValue | undefined): string =>
    CURRENCY_SYMBOLS[currency ?? CURRENCIES.EUR]

export const isAltCurrency = (currency: CurrencyValue | undefined): boolean =>
    (currency ?? CURRENCIES.EUR) !== CURRENCIES.EUR

// Validation constants
export const LOAN_VALIDATION = {
    // Plancher par ligne de prêt : le minimum assurable métier porte sur le capital couvert (loan-insured-amount.ts)
    MIN_AMOUNT: 1000,
    MAX_AMOUNT: 10000000,
    MIN_DURATION: 12, // 1 year
    MAX_DURATION_CLASSIC: 420, // 35 years
    MAX_DURATION_RELAY: 36, // 3 years
    MAX_DURATION_IN_FINE: 240, // 20 years
    MIN_RATE: 0,
    MAX_RATE: 15,
    MAX_DELAY_MONTHS: 240,
    MAX_STAGES: 6,
} as const

// Bounds live in loanFormSchema: a stage left over from a type switch must not block a loan that no longer shows stages
export const palierSchema = z.object({
    id: z.number().optional(),
    amount: z.number().optional(),
    month_duration: z.number().optional(),
})

export type PalierFormData = z.infer<typeof palierSchema>

export interface StoredStage {
    id?: number
    montant?: number | null
    duree?: number | null
    sum?: number
    period?: number
    amount?: number
    month_duration?: number
    sum_alt_currency?: number | null
}

// Validation reads amount/month_duration: stages reloaded with the API keys would look empty and block a complete quote
export const toFormStages = (stages: StoredStage[] | undefined) =>
    (stages ?? []).map((stage, index) => ({
        id: stage.id || index + 1,
        // `||` on purpose: legacy CHF stages store 0 in sum_alt_currency and the amount in montant.
        amount: stage.sum_alt_currency || (stage.montant ?? stage.sum ?? stage.amount ?? undefined),
        month_duration: stage.duree ?? stage.period ?? stage.month_duration ?? undefined,
    }))

export const getMaxDuration = (loanType: number): number => {
    if (loanType === LOAN_TYPES.RELAY) return LOAN_VALIDATION.MAX_DURATION_RELAY
    if (loanType === LOAN_TYPES.IN_FINE) return LOAN_VALIDATION.MAX_DURATION_IN_FINE
    return LOAN_VALIDATION.MAX_DURATION_CLASSIC
}

// Treat NaN (empty `valueAsNumber` inputs) and null (cleared Controller inputs — undefined would
// make RHF fall back to defaultValues) as empty so the object-level refinements below still run.
const emptyToUndefined = (value: unknown): unknown =>
    value === null || (typeof value === 'number' && Number.isNaN(value)) ? undefined : value

// Main loan form schema
export const loanFormSchema = z
    .object({
        // Loan type
        type: z
            .number({
                required_error: 'Veuillez sélectionner le type de prêt.',
            })
            .min(1, 'Veuillez sélectionner le type de prêt.'),

        // Amount fields (validations are conditional via .refine() below)
        amount: z.preprocess(emptyToUndefined, z.number().optional()),
        amount_left: z.preprocess(emptyToUndefined, z.number().optional()),

        // Duration fields (validations are conditional via .refine() below)
        duration: z.preprocess(emptyToUndefined, z.number().optional()),
        duration_left: z.preprocess(emptyToUndefined, z.number().optional()),

        // Interest rate (required via .refine() below; 0 stays valid, an unfilled field does not)
        rate: z.preprocess(
            emptyToUndefined,
            z
                .number()
                .min(LOAN_VALIDATION.MIN_RATE, `Le taux minimum est de ${LOAN_VALIDATION.MIN_RATE}%.`)
                .max(LOAN_VALIDATION.MAX_RATE, `Le taux d'intérêt doit être inférieur à ${LOAN_VALIDATION.MAX_RATE}%.`)
                .optional()
        ),

        // Reimbursement delay
        has_delay: z.boolean().default(false),

        delay_duration: z.preprocess(
            emptyToUndefined,
            z
                .number()
                .min(1, 'La durée du différé doit être supérieure à 0.')
                .optional()
        ),

        delay_type: z.enum([DELAY_TYPES.PARTIAL, DELAY_TYPES.TOTAL]).optional(),

        // Stages/Paliers (for STAGE and BUILDING loan types)
        stages: z.array(palierSchema).optional(),

        // Currency
        selected_currency: z.enum([CURRENCIES.EUR, CURRENCIES.CHF]).default(CURRENCIES.EUR),

        // Flag to know if user entered amount_left or initial amount
        save_amount_left: z.boolean().default(false),
    })
    // Conditional validation: amount is required if NOT in save_amount_left mode
    .refine(
        (data) => {
            if (!data.save_amount_left) {
                return data.amount !== undefined && data.amount >= LOAN_VALIDATION.MIN_AMOUNT
            }
            return true
        },
        {
            message: `Veuillez indiquer le montant du prêt (minimum ${LOAN_VALIDATION.MIN_AMOUNT} €).`,
            path: ['amount'],
        }
    )
    // Conditional validation: duration is required if NOT in save_amount_left mode
    .refine(
        (data) => {
            if (!data.save_amount_left) {
                return data.duration !== undefined && data.duration >= LOAN_VALIDATION.MIN_DURATION
            }
            return true
        },
        {
            message: `Veuillez indiquer la durée du prêt (minimum ${LOAN_VALIDATION.MIN_DURATION} mois).`,
            path: ['duration'],
        }
    )
    // Interest rate is required (0 is allowed once entered, an unfilled field is not)
    .refine((data) => data.rate !== undefined && data.rate !== null, {
        message: "Veuillez indiquer le taux d'intérêt.",
        path: ['rate'],
    })
    // Conditional validation: delay details required if has_delay is true
    .refine(
        (data) => {
            if (data.has_delay) {
                return data.delay_duration !== undefined && data.delay_duration > 0
            }
            return true
        },
        {
            message: 'Veuillez indiquer la durée du différé.',
            path: ['delay_duration'],
        }
    )
    // Conditional validation: delay_type required if has_delay is true
    .refine(
        (data) => {
            if (data.has_delay && data.delay_duration) {
                return data.delay_type !== undefined
            }
            return true
        },
        {
            message: 'Veuillez sélectionner le type de différé.',
            path: ['delay_type'],
        }
    )
    // Cap différé classique ; in fine/relais en sont exempts (durée du remboursement bornée par la durée du prêt)
    .refine(
        (data) => {
            if (data.type === LOAN_TYPES.IN_FINE || data.type === LOAN_TYPES.RELAY) return true
            if (data.has_delay && data.delay_duration) {
                return data.delay_duration <= LOAN_VALIDATION.MAX_DELAY_MONTHS
            }
            return true
        },
        {
            message: `Le différé maximum est de ${LOAN_VALIDATION.MAX_DELAY_MONTHS} mois.`,
            path: ['delay_duration'],
        }
    )
    // Validation: delay cannot exceed loan duration
    .refine(
        (data) => {
            const duration = data.duration || data.duration_left || 0
            if (data.has_delay && data.delay_duration) {
                return data.delay_duration < duration
            }
            return true
        },
        {
            message: 'Le différé doit être inférieur à la durée totale du prêt.',
            path: ['delay_duration'],
        }
    )
    // Conditional validation: amount_left is required if save_amount_left is true
    .refine(
        (data) => {
            if (data.save_amount_left) {
                return data.amount_left !== undefined
            }
            return true
        },
        {
            message: 'Veuillez indiquer le capital restant.',
            path: ['amount_left'],
        }
    )
    // Conditional validation: amount_left min only if save_amount_left is true
    .refine(
        (data) => {
            if (data.save_amount_left && data.amount_left !== undefined) {
                return data.amount_left >= LOAN_VALIDATION.MIN_AMOUNT
            }
            return true
        },
        {
            message: `Le montant minimum est de ${LOAN_VALIDATION.MIN_AMOUNT} €.`,
            path: ['amount_left'],
        }
    )
    // Conditional validation: duration_left is required if save_amount_left is true
    .refine(
        (data) => {
            if (data.save_amount_left) {
                return data.duration_left !== undefined
            }
            return true
        },
        {
            message: 'Veuillez indiquer la durée restante.',
            path: ['duration_left'],
        }
    )
    // Conditional validation: duration_left min only if save_amount_left is true
    .refine(
        (data) => {
            if (data.save_amount_left && data.duration_left !== undefined) {
                return data.duration_left >= 1
            }
            return true
        },
        {
            message: 'La durée restante doit être supérieure à 0.',
            path: ['duration_left'],
        }
    )
    // Max duration per loan type (in fine 240, relais 36), keyed by type id
    .superRefine((data, ctx) => {
        const duration = (data.save_amount_left ? data.duration_left : data.duration) ?? 0
        const maxDuration = getMaxDuration(data.type)
        if (duration > maxDuration) {
            const label =
                data.type === LOAN_TYPES.IN_FINE
                    ? 'in fine'
                    : data.type === LOAN_TYPES.RELAY
                    ? 'relais'
                    : 'de ce type'
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `La durée d'un prêt ${label} ne peut pas dépasser ${maxDuration} mois.`,
                path: [data.save_amount_left ? 'duration_left' : 'duration'],
            })
        }
    })
    .superRefine((data, ctx) => {
        if (!loanHasStages(data.type)) return
        const stages = data.stages ?? []
        const loanAmount = data.save_amount_left ? data.amount_left : data.amount
        const loanDuration = data.save_amount_left ? data.duration_left : data.duration

        stages.forEach((stage, index) => {
            const isAutoStage = index === stages.length - 1
            if (isAutoStage) {
                // The last stage is computed from the remaining duration, 0 once the other stages fill the loan
                if (stages.length > 1 && loanAmount && loanDuration && (stage.month_duration ?? 0) < 1) {
                    ctx.addIssue({
                        code: 'custom',
                        message: 'Les paliers couvrent toute la durée du prêt : laissez au moins 1 mois au dernier palier.',
                        path: ['stages', index, 'month_duration'],
                    })
                }
                return
            }
            if (stage.amount === undefined || stage.amount < 0) {
                ctx.addIssue({
                    code: 'custom',
                    message: `Veuillez indiquer le montant du palier ${index + 1}.`,
                    path: ['stages', index, 'amount'],
                })
            }
            if (stage.month_duration === undefined || stage.month_duration < 1) {
                ctx.addIssue({
                    code: 'custom',
                    message: `Veuillez indiquer la durée du palier ${index + 1} (1 mois minimum).`,
                    path: ['stages', index, 'month_duration'],
                })
            }
        })
    })

// Infer TypeScript type from schema
export type LoanFormData = z.infer<typeof loanFormSchema>

// Default values factory
export const createLoanFormDefaults = (): Partial<LoanFormData> => ({
    type: LOAN_TYPES.CLASSIC, // Default to classic loan type
    amount: undefined,
    amount_left: undefined,
    duration: undefined,
    duration_left: undefined,
    rate: undefined,
    has_delay: false,
    delay_duration: undefined,
    delay_type: undefined,
    stages: [],
    selected_currency: CURRENCIES.EUR,
    save_amount_left: false,
})

// Safe parse with detailed error handling
export const parseLoanForm = (data: unknown) => {
    return loanFormSchema.safeParse(data)
}

// Export validators for individual fields
export const loanValidators = {
    type: z.number().min(1),
    amount: z.number().min(LOAN_VALIDATION.MIN_AMOUNT).max(LOAN_VALIDATION.MAX_AMOUNT),
    duration: z.number().min(LOAN_VALIDATION.MIN_DURATION),
    rate: z.number().min(LOAN_VALIDATION.MIN_RATE).max(LOAN_VALIDATION.MAX_RATE),
} as const

// Helper functions
export const isStaggeredLoan = (loanType: number): boolean => {
    return loanType === LOAN_TYPES.STAGE
}

export const isBuildingLoan = (loanType: number): boolean => {
    return loanType === LOAN_TYPES.BUILDING
}

export const isInFineLoan = (loanType: number): boolean => {
    return loanType === LOAN_TYPES.IN_FINE
}

export const isRelayLoan = (loanType: number): boolean => {
    return loanType === LOAN_TYPES.RELAY
}

export const loanHasStages = (loanType: number): boolean => {
    return isStaggeredLoan(loanType) || isBuildingLoan(loanType)
}

export const showDelayFields = (loanType: number): boolean => {
    // In fine and relay loans don't have separate delay fields
    return !isInFineLoan(loanType) && !isRelayLoan(loanType)
}

// Format helpers
export const formatDurationToYearsAndMonths = (months: number): string => {
    const years = Math.floor(months / 12)
    const remainingMonths = months % 12

    if (years === 0) {
        return `${remainingMonths} mois`
    }

    if (remainingMonths === 0) {
        return `${years} an${years > 1 ? 's' : ''}`
    }

    return `${years} an${years > 1 ? 's' : ''} et ${remainingMonths} mois`
}

export const formatCurrency = (amount: number, currency: CurrencyValue = CURRENCIES.EUR): string => {
    const symbols: Record<CurrencyValue, string> = {
        [CURRENCIES.EUR]: '€',
        [CURRENCIES.CHF]: 'CHF',
    }

    return `${amount.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${symbols[currency]}`
}

// Calculate monthly payment with interest (simple approximation)
export const calculateMonthlyPayment = (principal: number, annualRate: number, durationMonths: number): number => {
    if (annualRate === 0) {
        return principal / durationMonths
    }

    const monthlyRate = annualRate / 100 / 12
    const payment =
        (principal * (monthlyRate * Math.pow(1 + monthlyRate, durationMonths))) /
        (Math.pow(1 + monthlyRate, durationMonths) - 1)

    return Math.round(payment * 100) / 100
}

// Calculate total cost with interest
export const calculateTotalCost = (principal: number, annualRate: number, durationMonths: number): number => {
    const monthlyPayment = calculateMonthlyPayment(principal, annualRate, durationMonths)
    return Math.round(monthlyPayment * durationMonths * 100) / 100
}
